import { beSmsMessageGetAllMessages } from 'Api/beSmsMessageApi'
import { STORE_KEY_ID_LIST } from 'Constants/'
import getIntl from 'Helpers/intl'
import { storeGet } from 'Helpers/localStorage'
import { MESSAGE_BOX_VARIANT_DANGER } from 'mailup-web-components'
import { useEffect } from 'react'
import {
  atom,
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState
} from 'recoil'

export const TABLE_COLUMNS = [
  {
    field: 'Subject',
    label: getIntl().formatMessage({
      id: 'Load.SelectEmailMessage.SubjectHeaderColumn'
    }),
    sortable: true,
    span: 8
  },
  {
    field: 'LastUpdated',
    label: getIntl().formatMessage({
      id: 'Load.WidgetLastEditedMessage.LastModifiedLabel'
    }),
    sortable: true,
    span: 4
  }
]

export const modalSmsMessagesPaginationState = atom({
  key: 'modalSmsMessagesPaginationState',
  default: {
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0
  }
})

export const modalSmsMessagesSearchState = atom({
  key: 'modalSmsMessagesSearchState',
  default: {
    searchString: '',
    orderBy: 'Subject',
    order: 'ASC'
  }
})

export const modalSmsMessagesState = atom({
  key: 'modalSmsMessagesState',
  default: {
    items: undefined,
    loading: true,
    loaded: false,
    error: false,
    errMessage: '',
    severity: MESSAGE_BOX_VARIANT_DANGER,
    selectedMessage: undefined,
    resetIsRequired: false,
    show: false,
    droppableId: '',
    selectedItem: undefined,
    fetchAfterCopy: false,
    showSmsLoader: false
  }
})

export async function fetchSMSMessages(
  pageNumber,
  pageSize,
  searchString,
  orderBy,
  orderDescending
) {
  const idList = storeGet(STORE_KEY_ID_LIST)

  let filterBy = ''
  if (searchString && searchString !== '') {
    const trimmedSearchText = searchString
      .trim()
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
    filterBy = `${trimmedSearchText}`
  }

  const { Items, TotalItems } = await beSmsMessageGetAllMessages(
    idList,
    {
      pageNumber,
      pageSize,
      orderDescending,
      orderBy
    },
    {
      Subject: filterBy,
      Tags: []
    }
  )
  return { Items, TotalItems }
}

const useModalSmsMessages = () => {
  const [
    {
      show,
      items,
      loaded,
      loading,
      error,
      errMessage,
      severity,
      resetIsRequired,
      fetchAfterCopy
    },
    setSmsState
  ] = useRecoilState(modalSmsMessagesState)

  const { pageNumber, pageSize } = useRecoilValue(
    modalSmsMessagesPaginationState
  )
  const { searchString, order, orderBy } = useRecoilValue(
    modalSmsMessagesSearchState
  )

  const resetSmsPagination = useResetRecoilState(
    modalSmsMessagesPaginationState
  )
  const resetSmsSearch = useResetRecoilState(modalSmsMessagesSearchState)

  const fetchEmailCallback = useRecoilCallback(
    ({ snapshot, set }) =>
      async (action) => {
        set(modalSmsMessagesState, (prev) => ({
          ...prev,
          loading: true,
          fetchAfterCopy: false // reset
        }))

        const { pageNumber, pageSize } = await snapshot.getPromise(
          modalSmsMessagesPaginationState
        )
        const { searchString, orderBy, order } = await snapshot.getPromise(
          modalSmsMessagesSearchState
        )

        try {
          const itemList = await fetchSMSMessages(
            pageNumber,
            pageSize,
            searchString,
            orderBy,
            order === 'DESC'
          )

          set(modalSmsMessagesState, (prev) => ({
            ...prev,
            items: itemList.Items
          }))
          set(modalSmsMessagesPaginationState, (prev) => ({
            ...prev,
            totalItems: itemList.TotalItems
          }))
        } catch (error) {
          set(modalSmsMessagesState, (prev) => ({
            ...prev,
            error: true,
            errMessage: getIntl().formatMessage({
              id: 'Load.Alerts.ErrorGeneric'
            }),
            severity: MESSAGE_BOX_VARIANT_DANGER
          }))
        }

        set(modalSmsMessagesState, (prev) => ({
          ...prev,
          loading: false,
          loaded: true
        }))
      },
    []
  )

  useEffect(() => {
    !loaded && fetchEmailCallback()
  }, [])

  useEffect(() => {
    if (!show && resetIsRequired) {
      resetSmsPagination()
      resetSmsSearch()
    }
  }, [show])

  useEffect(() => {
    fetchAfterCopy && fetchEmailCallback()
  }, [fetchAfterCopy])

  useEffect(() => {
    loaded && show && fetchEmailCallback()
    setSmsState((prev) => ({
      ...prev,
      resetIsRequired: loaded
    }))
  }, [searchString, pageSize, pageNumber, order, orderBy, show])

  return {
    show,
    items,
    loaded,
    loading,
    error,
    errMessage,
    severity
  }
}

export default useModalSmsMessages
